import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import { useBusinessTypeContext } from '../../contexts/BusinessTypeContext';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import apiClient, { getErrorMessage } from '../../utils/api';
import { GoatShed } from '../../types/api';

const GoatShedsPage: React.FC = () => {
  const { selectedLocation } = useLocation();
  const { businessType } = useBusinessTypeContext();
  const { user } = useAuth();
  const [sheds, setSheds] = useState<GoatShed[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    if (selectedLocation?.id && businessType === 'goat') {
      loadSheds();
    }
  }, [selectedLocation?.id, businessType]);

  const loadSheds = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/goat/sheds?location_id=${selectedLocation?.id}`);
      setSheds(response.data || []);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to load goat sheds'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (shedId: string, shedName: string) => {
    if (user?.role !== 'admin') {
      setError('Only admins can delete sheds');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete shed "${shedName}"?`)) {
      return;
    }

    try {
      setDeletingId(shedId);
      await apiClient.delete(`/goat/sheds/${shedId}`);
      await loadSheds();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to delete shed'));
    } finally {
      setDeletingId(null);
    }
  };

  if (businessType !== 'goat') {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">This page is only available for goat farming locations.</p>
        <Link to="/dashboard" className="text-blue-600 hover:text-blue-800">Go to Dashboard</Link>
      </div>
    );
  }

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">Go to Location Selection</Link>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/goat/logs" className="btn-touch p-2 text-gray-400 hover:text-gray-600">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">🏠 Goat Sheds</h1>
            <p className="text-gray-600">{selectedLocation.name}</p>
          </div>
        </div>
        <Link
          to="/goat/sheds/new"
          className="btn-touch bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Add Shed</span>
        </Link>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Sheds List */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Sheds ({sheds.length})
        </h2>

        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : sheds.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sheds.map((shed) => (
              <div key={shed.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{shed.name}</h3>
                    {shed.capacity && (
                      <p className="text-sm text-gray-600">Capacity: {shed.capacity} goats</p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    {user?.role === 'admin' && (
                      <>
                        <Link
                          to={`/goat/sheds/${shed.id}/edit`}
                          className="text-blue-600 hover:text-blue-800"
                          title="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={() => handleDelete(shed.id, shed.name)}
                          disabled={deletingId === shed.id}
                          className="text-red-600 hover:text-red-800 disabled:opacity-50"
                          title="Delete"
                        >
                          {deletingId === shed.id ? (
                            <LoadingSpinner size="sm" />
                          ) : (
                            <TrashIcon className="h-4 w-4" />
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {shed.description && (
                  <p className="text-sm text-gray-600 mb-2">{shed.description}</p>
                )}
                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    shed.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {shed.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <span className="text-xs text-gray-500">
                    Created: {new Date(shed.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">🏠</div>
            <p className="text-gray-500 mb-4">No sheds found</p>
            <Link
              to="/goat/sheds/new"
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add First Shed
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoatShedsPage;
